import { useState } from 'react';
import type { AttemptResolution, PaymentAttempt, PayableInvoice } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatCardRef, formatPropertyLabel } from '../../lib/payments';

interface AttemptOutcomeProps {
  attempt: PaymentAttempt;
  invoice: PayableInvoice;
  /** Success/dry run: move on to the next invoice. Failure/unknown: back to the queue. */
  onNext: () => void;
  /** Human resolution of an `unknown` attempt, after checking Noqodi. */
  onResolve: (resolution: AttemptResolution, note: string) => void;
  resolving?: boolean;
  resolveError?: string | null;
}

function Summary({ attempt, invoice }: { attempt: PaymentAttempt; invoice: PayableInvoice }) {
  return (
    <p className="text-sm text-gray-500 mt-1">
      {formatCurrency(attempt.amount, attempt.currency)} to {invoice.biller ?? '—'} ({formatPropertyLabel(invoice)}),
      card {formatCardRef(attempt.cardLast4)}.
    </p>
  );
}

/**
 * The terminal states of a payment attempt (CHO-754), rendered distinctly:
 *
 * - succeeded: confirmed result, then the operator moves on.
 * - failed: the service's reason, and we stay put — never auto-advance past
 *   a failure, and there is deliberately no retry button anywhere.
 * - dry_run: the service ran in DRY_RUN mode — the portal was walked but
 *   nothing was submitted. NOT a payment, NOT ambiguous: no money moved and
 *   the invoice returns to the queue unblocked.
 * - unknown: NOT an ordinary failure. The service could not confirm whether
 *   money left the account, so the invoice is blocked until the operator
 *   checks Noqodi and marks the outcome here. The service requires a note.
 */
export function AttemptOutcome({ attempt, invoice, onNext, onResolve, resolving = false, resolveError = null }: AttemptOutcomeProps) {
  const [resolution, setResolution] = useState<AttemptResolution>('failed');
  const [note, setNote] = useState('');

  if (attempt.status === 'succeeded') {
    return (
      <div className="bg-white border border-green-200 rounded-lg p-6 max-w-xl">
        <h2 className="text-lg font-semibold text-green-800">Payment succeeded</h2>
        <Summary attempt={attempt} invoice={invoice} />
        <p className="text-sm text-gray-700 mt-3">
          The payment is confirmed. Take the next invoice when you are ready.
        </p>
        <button
          type="button"
          onClick={onNext}
          className="mt-5 px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700"
        >
          Next invoice
        </button>
      </div>
    );
  }

  if (attempt.status === 'failed') {
    return (
      <div className="bg-white border border-red-200 rounded-lg p-6 max-w-xl">
        <h2 className="text-lg font-semibold text-red-800">Payment failed</h2>
        <Summary attempt={attempt} invoice={invoice} />
        <p role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {attempt.failureReason || 'The service did not give a reason.'}
        </p>
        <p className="text-sm text-gray-600 mt-3">
          This invoice was not paid. It stays in the queue — re-attempting is a deliberate decision from there, never
          an automatic retry.
        </p>
        <button
          type="button"
          onClick={onNext}
          className="mt-5 px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700"
        >
          Back to queue
        </button>
      </div>
    );
  }

  if (attempt.status === 'dry_run') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-xl">
        <h2 className="text-lg font-semibold text-gray-800">Dry run — no payment submitted</h2>
        <Summary attempt={attempt} invoice={invoice} />
        <p className="text-sm text-gray-700 mt-3">
          The payment service is running in dry-run mode: it walked the portal but submitted nothing. No money moved,
          and this invoice is back in the queue, unblocked.
        </p>
        <button
          type="button"
          onClick={onNext}
          className="mt-5 px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700"
        >
          Back to queue
        </button>
      </div>
    );
  }

  // status === 'unknown' (or a terminal state we do not recognise — treat the
  // same way: unconfirmed money movement is never presented as an ordinary failure).
  const noteMissing = note.trim().length === 0;
  return (
    <div className="bg-white border-2 border-amber-300 rounded-lg p-6 max-w-xl">
      <h2 className="text-lg font-semibold text-amber-900">Payment status unknown</h2>
      <Summary attempt={attempt} invoice={invoice} />
      <p className="text-sm text-gray-800 mt-3">
        We could not confirm whether this payment went through. The money may already have left the account — so this
        is <strong>not</strong> an ordinary failure.
      </p>
      <p className="text-sm text-gray-800 mt-2">
        <strong>This invoice is blocked until you resolve it.</strong> Check Noqodi&rsquo;s transaction history, then
        record what you found below.
      </p>

      <fieldset className="mt-4 space-y-2 text-sm text-gray-800">
        <legend className="sr-only">What did Noqodi show?</legend>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="resolution"
            className="mt-1"
            checked={resolution === 'succeeded'}
            onChange={() => setResolution('succeeded')}
          />
          <span>The payment did go through — record it as paid</span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name="resolution"
            className="mt-1"
            checked={resolution === 'failed'}
            onChange={() => setResolution('failed')}
          />
          <span>The payment did not go through — unblock the invoice so it can be paid again</span>
        </label>
      </fieldset>

      <label className="block mt-3 text-sm text-gray-600">
        Note — required: what Noqodi&rsquo;s history showed
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          required
          placeholder="No matching transaction in Noqodi history"
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </label>

      {resolveError && (
        <p role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {resolveError}
        </p>
      )}

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => onResolve(resolution, note.trim())}
          disabled={resolving || noteMissing}
          className="px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {resolving ? 'Marking resolved…' : 'Mark resolved'}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={resolving}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
        >
          Back to queue
        </button>
      </div>
    </div>
  );
}
